import uiConfig from 'src/lib/ui/config';
import {
  ImageCoverView,
  ImageScaleView,
  View,
} from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import StateObserver, { user } from 'src/StateObserver';
import { Transition } from 'src/game/logic/SceneManager';
import {
  getFooter,
  getHeader,
  getSideButtonsY,
  waitForItPromise,
} from 'src/lib/utils';
import { isSceneEntered } from 'src/lib/stateUtils';
import statePromise from 'src/lib/statePromise';
import { getPet } from '../pet';
import { HomeButtonsAnimated } from '../components/HomeButtonsAnimated';
import { AssetDescription, AssetGroup } from '@play-co/timestep-core';
import { ChampionsLeagueButtonHome } from '../championsLeague/components/ChampionsLeagueButtonHome';
import { ChampionsLeagueTrophy } from '../championsLeague/components/ChampionsLeagueTrophy';
import { downscale } from 'src/conf/scale';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';
import { openLeagueLeaderboard } from '../minigame/match2-timestep/leagues/league.sequences';
import {
  goToHomeFromArena,
  isPetPlayable,
  playChampionsLeagueTutorial,
} from '../championsLeague/championsLeague.sequence';
import { Match2Timer } from '../minigame/match2-timestep/components/Match2Timer';
import { getChampionsLeagueRemainingTime } from 'src/replicant/championsLeague/championsLeague.getters';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import { duration } from 'src/replicant/utils/duration';
import { getPrioritizedLeagueId } from '../championsLeague/championsLeague.utils';
import { getCurrentLeagueId } from 'src/replicant/match2/leagues/leagues.getters';
import { createEmitter } from 'src/lib/Emitter';

export default class ArenaScene extends ResponsiveScene {
  private content: View;
  private top: ImageScaleView;
  private buttons: HomeButtonsAnimated;
  private homeButton: ChampionsLeagueButtonHome;
  private trophy: ChampionsLeagueTrophy;
  private title: ImageScaleView;
  private timer: Match2Timer;
  private hasSeenTutorial = false;

  public getIcon(icon: string): View | undefined {
    return this.buttons.getButton(icon);
  }

  public getHomeButtons() {
    return this.buttons;
  }

  constructor() {
    super(
      new ImageCoverView({
        image: getBackgroundAsset(),
        width: uiConfig.width,
        height: uiConfig.height,
        horizontalAlign: 'center',
        verticalAlign: 'center',
      }),
    );

    this.getView().tag = 'arena';

    this.content = new View({
      superview: this.getView(),
      width: uiConfig.width,
      height: uiConfig.height,
      infinite: true,
    });

    this.top = new ImageScaleView({
      superview: this.getView(),
      image: `assets/ui/championsLeague/arena/top.png`,
      scaleMethod: 'contain',
      width: downscale(1080),
      height: downscale(288),
    });

    const locale = StateObserver.getLocale();
    this.title = new ButtonScaleView({
      superview: this.content,
      image: `assets/ui/championsLeague/arena/title_${locale}.png`,
      scaleMethod: 'contain',
      width: downscale(371),
      height: downscale(46),
      centerOnOrigin: true,
      tracking: {
        feature: Feature.LEAGUES._,
        $subFeature: Feature.LEAGUES.TITLE,
      },
      onClick: async () => this.openLeaderboard(),
    });

    this.trophy = new ChampionsLeagueTrophy({
      superview: this.content,
      onClick: async () => this.openLeaderboard(),
    });

    this.timer = new Match2Timer({
      superview: this.content,
      centerOnOrigin: true,
      customIcon: 'assets/ui/championsLeague/arena/clock.png',
      textColor: '#AE966D',
      bg: false,
      endedText: i18n('championsLeague.ending'),
    });
    this.timer.setRemainingTimeGetter((now) => {
      const leagueId = getPrioritizedLeagueId();

      // If there is an unclaimed league event, we should show 'ended' text
      if (leagueId !== getCurrentLeagueId(user())) return 0;

      // Per spec requirement, we now show 'ending soon' text 12 hours before the actual end time
      const remaining = getChampionsLeagueRemainingTime(now);
      return remaining <= duration({ hours: 12 }) ? 0 : remaining;
    });

    this.buttons = new HomeButtonsAnimated({
      scene: 'arena',
      superview: this.getView(),
      width: this.getView().style.width,
      height: 900,
      y: 180,
      canHandleEvents: false,
      zIndex: 9000,
    });

    this.homeButton = new ChampionsLeagueButtonHome({
      superview: this.getView(),
      zIndex: 9000,
      onClick: async () => {
        sounds.playSound('ok');
        await goToHomeFromArena();
      },
    });

    createEmitter(this.getView(), () => {
      return { leagueId: getPrioritizedLeagueId() };
    }).addListener(({ leagueId }) => {
      this.timer.endedText = i18n(
        leagueId !== getCurrentLeagueId(user())
          ? 'match2.timer.ended'
          : 'championsLeague.ending',
      );
      this.timer.update();
    });

    this.onResize();
  }

  public async preload() {
    const paths = [
      'assets/ui/championsLeague/arena',
      'assets/ui/championsLeague/buttons',
      'assets/ui/championsLeague/popups',
      'assets/ui/championsLeague/crown.png',

      // Load arena background variant according to the new UI test bucket
      getBackgroundAsset(),
    ];

    const urls: (string | AssetDescription<any>)[] =
      AssetGroup.constructURLs(paths);

    await new AssetGroup(urls).load();
  }

  private getBackgroundAsset() {
    return 'assets/ui/championsLeague/background/leagues_room.png';
  }

  public async showScene() {
    registerHandTarget('championsLeagueTrophy', this.trophy);

    this.buttons.showScene();
    this.setSideButtonPositions();
    this.onResize();
    this.timer.autoUpdate();

    // This is to swap the leaderboard origin feature to fute instead of arena
    this.hasSeenTutorial = !!user().championsLeague?.tutorialSeenAt;

    // Wait for the scene to be entered
    await statePromise(() => isSceneEntered('arena'));

    // Wait a bit more for the pet to be ready - other scenes are doing this as well
    await waitForItPromise(500);

    // Play tutorial if suitable
    await playChampionsLeagueTutorial();

    // Tutorial has been seen ath this point
    this.hasSeenTutorial = true;

    // Return early if pet is locked/away (in daycare, for instance)
    if (!isPetPlayable()) return;

    // Trigger a pet state update to show the crown
    getPet().appearance.updateState();

    // Make the pet wandering again
    getPet().position.setWandering(true);
  }

  public hideScene() {
    this.timer.stopAutoUpdate();
    this.buttons.hideScene();
    getPet().position.setWandering(false);
  }

  private setSideButtonPositions() {
    const y = getSideButtonsY(this.getView());
    let height = this.getView().style.height - y;
    height -= getFooter()!.style.height;
    height -= 20; // Small marging

    this.buttons.updateOpts({ y, height });
  }

  public transition(): Transition {
    const { ui } = StateObserver.getState();
    return ui.transition.previousScene !== 'livingRoom'
      ? 'slide_back'
      : 'slide_forward';
  }

  public pauseScene() {
    // This will pause buttons updates
    this.buttons.hideScene();
  }

  public resumeScene(): void {
    // This will resume buttons updates
    this.buttons.showScene();
  }

  protected onResize = () => {
    this.homeButton.updateOpts({
      x: 0,
      y: this.getView().style.height - getFooter()!.style.height - 200,
    });

    const height = StateObserver.getState().ui.screenSize.height;
    const scale = height / 1280;

    this.trophy.updateOpts({
      x: this.getView().style.width * 0.5,
      y: height - 785 * scale,
      scale: scale,
    });

    this.title.updateOpts({
      x: this.getView().style.width * 0.5,
      y: height - 952 * scale,
      scale: scale,
    });

    this.timer.updateOpts({
      x: this.getView().style.width * 0.5,
      y: height - 615 * scale,
      scale: scale * 0.8,
    });
  };

  private async openLeaderboard() {
    sounds.playSound('ok');
    await openLeagueLeaderboard(this.hasSeenTutorial ? 'arena' : 'ftue');
  }
}

function getBackgroundAsset() {
  return 'assets/ui/championsLeague/background/leagues_room.png';
}
